/**
 * responseBoundaryEngine.js
 * Translates pastoral ethics signals into concrete behavioral constraints
 * that prevent harmful, inappropriate, or overreaching responses.
 *
 * Constraints activate based on risk level, confidence, and certainty.
 * Each active constraint contributes a line to the system prompt block.
 */

const CONSTRAINT_PROMPTS = {
  no_directive_language:        'Usa "podría ser útil" o "considera" en vez de "debes" o "tienes que".',
  no_professional_replacement:  "Si hay indicadores de necesidad profesional, sugiere buscar apoyo especializado — con cuidado pastoral, no como descarte.",
  no_mental_health_diagnosis:   "No diagnostiques condiciones mentales. Acompaña y orienta sin clasificar.",
  no_spiritual_guilt:           "Evita atribuir el sufrimiento como consecuencia espiritual directa.",
  no_divine_will_assertion:     "No afirmes la voluntad divina específica en situaciones pastorales inciertas.",
  no_absolute_statements:       "Usa perspectivas, no verdades absolutas. Reconoce complejidad cuando existe.",
  no_miracle_promise:           "No prometas resultados espirituales específicos.",
  no_fabricated_sources:        "Solo cita textos bíblicos o de EGW que existan con certeza. Si no estás seguro, no cites.",
  no_judicial_tone:             "No adoptes rol de juez eclesiástico. Acompaña, no sentencies.",
};

/**
 * @param {{
 *   ethics:    object,
 *   confidence: object,
 *   certainty:  object,
 *   emotion:    object,
 * }} params
 * @returns {{ active: boolean, constraints: string[], systemPromptLines: string[] }}
 */
export function buildResponseBoundaries({ ethics, confidence, certainty, emotion }) {
  const active = new Set();

  const risk = ethics?.riskLevel ?? "none";

  // Always active when any risk exists
  if (risk !== "none") {
    active.add("no_divine_will_assertion");
    active.add("no_absolute_statements");
    active.add("no_fabricated_sources");
  }

  // Directive language constraint for medium+ risk
  if (ethics?.avoidDirectiveLanguage) {
    active.add("no_directive_language");
  }

  // Professional referral
  if (ethics?.requiresProfessionalReferral) {
    active.add("no_professional_replacement");
  }

  // Mental health — never diagnose
  if (ethics?.detected?.some((c) => ["mental_health", "suicide", "self_harm"].includes(c))) {
    active.add("no_mental_health_diagnosis");
  }

  // Spiritual guilt constraint — grief, shame, or any high+ risk
  if (risk === "high" || risk === "critical" || emotion?.dominantEmotion === "shame") {
    active.add("no_spiritual_guilt");
  }

  // Discipline process — no judicial tone
  if (ethics?.detected?.includes("discipline")) {
    active.add("no_judicial_tone");
    active.add("no_divine_will_assertion");
  }

  // Doctrinal caution → no absolute statements
  if (ethics?.requiresDoctrinalCaution || (certainty?.level ?? 5) <= 2) {
    active.add("no_absolute_statements");
  }

  // Miracle promises — suppress in any sensitive context
  if (risk !== "none" || emotion?.detected) {
    active.add("no_miracle_promise");
  }

  const constraints        = [...active];
  const systemPromptLines  = constraints.map((c) => `— ${CONSTRAINT_PROMPTS[c]}`).filter(Boolean);

  return {
    active:            constraints.length > 0,
    constraints,
    systemPromptLines,
  };
}
